/**
 * Submit Title Distribution Module
 * Handles distribution territories and rights selection
 */
(function () {
  "use strict";

  // Run once per page (see submission-session-manager.js).
  if (window.submitTitleDistributionLoaded) {
    return;
  }
  window.submitTitleDistributionLoaded = true;

  function getApplyBasePath() {
    return window.submissionApplyBasePath || "/partners/bloodstream/apply";
  }

  const sessionManager = window.SubmissionSessionManager;

  if (!document.getElementById("submit-title-distribution")) {
    return;
  }

  /**
   * Initialize the module
   */
  function init() {
    const distributionPath = `${getApplyBasePath()}/distribution`;
    if (window.location.pathname !== distributionPath) {
      return;
    }

    if (!document.getElementById("launch_time")) {
      return;
    }

    if (!sessionManager) {
      console.error("Session manager not loaded");
      return;
    }

    // Load existing session or redirect if none
    if (!sessionManager.hasSession()) {
      console.error("No session found, redirecting to start");
      window.location.href = `${getApplyBasePath()}/imdb`;
      return;
    }

    // Check if session was already submitted and redirect if so
    if (sessionManager.checkAndRedirectIfSubmitted()) {
      return; // Exit init - redirect is happening
    }

    // Set current step
    sessionManager.setCurrentStep(4);

    // Load title information card
    loadTitleInfoCard();

    // Load saved data if exists
    loadSavedData();

    // Setup form handlers
    setupFormHandlers();
    setupNavigation();

    // Initial validation
    validateForm();

    // Update progress
    updateProgress();
  }

  /**
   * Load and display title information in sidebar card
   */
  function loadTitleInfoCard() {
    const step1Data = sessionManager.getStepData(1);
    const step3Data = sessionManager.getStepData(3);

    if (!step1Data.title_name) {
      return;
    }

    const titleName = document.getElementById("title-name");
    const titleYear = document.getElementById("title-year");
    const titleGenres = document.getElementById("title-genres");
    const titlePoster = document.getElementById("title-poster");
    const titlePosterImg = document.getElementById("title-poster-img");

    // Update title name (decode HTML entities)
    if (titleName) {
      const decodedTitle = window.HtmlEntityDecoder
        ? window.HtmlEntityDecoder.decode(
            step1Data.title_name || "Unknown Title"
          )
        : step1Data.title_name || "Unknown Title";
      titleName.textContent = decodedTitle;
    }

    // Update year
    if (titleYear && step1Data.year) {
      titleYear.textContent = `Year: ${step1Data.year}`;
    }

    // Update genres
    if (titleGenres && step1Data.genres) {
      titleGenres.textContent = `Genres: ${step1Data.genres}`;
    }

    // Use customization poster if available
    let posterUrl = step1Data.poster_url;

    if (step3Data.custom_poster_url && step3Data.custom_poster_data) {
      posterUrl = step3Data.custom_poster_data.url;
    } else if (step3Data.selected_poster) {
      posterUrl = step3Data.selected_poster;
    }

    // Show and load poster image if available
    if (titlePosterImg && posterUrl) {
      titlePosterImg.src = posterUrl;
      const decodedTitleForAlt = window.HtmlEntityDecoder
        ? window.HtmlEntityDecoder.decode(step1Data.title_name)
        : step1Data.title_name;
      titlePosterImg.alt = `${decodedTitleForAlt} Poster`;

      if (titlePoster) {
        titlePoster.classList.remove("hide");
      }

      titlePosterImg.onload = () => console.log("Poster loaded successfully");
      titlePosterImg.onerror = () => {
        console.log("Failed to load poster image");
        if (titlePoster) titlePoster.classList.add("hide");
      };
    }
  }

  /**
   * Load saved data from previous session
   */
  function loadSavedData() {
    const step4Data = sessionManager.getStepData(4);

    const setValueIfExists = (id, value) => {
      const element = document.getElementById(id);
      if (element) {
        element.value = value;
      }
    };

    const toggleHiddenById = (id, shouldShow) => {
      const element = document.getElementById(id);
      if (!element) {
        return;
      }
      element.classList.toggle("hide", !shouldShow);
    };

    // Load territory selection
    if (step4Data.territory) {
      const territoryRadio = document.getElementById(
        `territory_${step4Data.territory}`
      );
      if (territoryRadio) {
        territoryRadio.checked = true;
        // Show custom territory field if "other" was selected
        if (step4Data.territory === "other") {
          toggleHiddenById("custom-territory-container", true);
        }
      }
    } else {
      // No saved data - ensure default "Worldwide" is saved
      const worldwideRadio = document.getElementById("territory_worldwide");
      if (worldwideRadio && worldwideRadio.checked) {
        // Save the default selection to session
        saveFormData();
      }
    }

    // Load custom territory text
    if (step4Data.custom_territory) {
      setValueIfExists("custom_territory", step4Data.custom_territory);
    }

    // Load distribution notes
    if (step4Data.distribution_notes) {
      setValueIfExists("distribution_notes", step4Data.distribution_notes);
    }

    // Load launch timing
    if (step4Data.launch_timing) {
      const launchRadio = document.getElementById(
        `launch_${step4Data.launch_timing}`
      );
      if (launchRadio) {
        launchRadio.checked = true;
        // Show date/time fields if "specific_date" was selected
        if (step4Data.launch_timing === "specific_date") {
          toggleHiddenById("launch-date-container", true);
        }
      }
    } else {
      // No saved data - ensure default "ASAP" is saved
      const asapRadio = document.getElementById("launch_asap");
      if (asapRadio && asapRadio.checked) {
        // Save the default selection to session
        saveFormData();
      }
    }

    // Load launch date (time is always 00:00 midnight PST)
    if (step4Data.launch_date) {
      setValueIfExists("launch_date", step4Data.launch_date);
    }
    // Ensure launch_time is always set to midnight
    setValueIfExists("launch_time", "00:00");

    // Load contact information
    if (step4Data.contact_name) {
      setValueIfExists("contact_name", step4Data.contact_name);
    }
    if (step4Data.contact_email) {
      setValueIfExists("contact_email", step4Data.contact_email);
    }
    if (step4Data.contact_phone) {
      setValueIfExists("contact_phone", step4Data.contact_phone);
    }
    if (step4Data.contact_company) {
      setValueIfExists("contact_company", step4Data.contact_company);
    }

    // Load address fields
    if (step4Data.address_line1) {
      setValueIfExists("address_line1", step4Data.address_line1);
    }
    if (step4Data.address_line2) {
      setValueIfExists("address_line2", step4Data.address_line2);
    }
    if (step4Data.city) {
      setValueIfExists("city", step4Data.city);
    }
    if (step4Data.state) {
      setValueIfExists("state", step4Data.state);
    }
    if (step4Data.postal_code) {
      setValueIfExists("postal_code", step4Data.postal_code);
    }
    if (step4Data.country) {
      setValueIfExists("country", step4Data.country);
    }
  }

  /**
   * Setup form handlers
   */
  function setupFormHandlers() {
    const setValueIfExists = (id, value) => {
      const element = document.getElementById(id);
      if (element) {
        element.value = value;
      }
    };

    // Territory radio buttons
    const territoryRadios = document.querySelectorAll(
      'input[name="territory"]'
    );
    territoryRadios.forEach((radio) => {
      radio.addEventListener("change", async function () {
        const customContainer = document.getElementById(
          "custom-territory-container"
        );
        if (!customContainer) {
          return;
        }

        if (this.value === "other") {
          customContainer.classList.remove("hide");
        } else {
          customContainer.classList.add("hide");
          setValueIfExists("custom_territory", "");
        }
        await saveFormData();
        validateForm();
      });
    });

    // Launch timing radio buttons
    const launchRadios = document.querySelectorAll(
      'input[name="launch_timing"]'
    );
    launchRadios.forEach((radio) => {
      radio.addEventListener("change", async function () {
        const dateContainer = document.getElementById("launch-date-container");
        if (!dateContainer) {
          return;
        }

        if (this.value === "specific_date") {
          dateContainer.classList.remove("hide");
        } else {
          dateContainer.classList.add("hide");
          setValueIfExists("launch_date", "");
          setValueIfExists("launch_time", "00:00");
        }
        await saveFormData();
        validateForm();
      });
    });

    // Text inputs
    const textInputs = ["custom_territory", "distribution_notes"];
    textInputs.forEach((fieldId) => {
      const field = document.getElementById(fieldId);
      if (field) {
        field.addEventListener("blur", async () => await saveFormData());
        field.addEventListener("input", validateForm);
      }
    });

    // Date input (time is now hidden, defaults to 00:00)
    const dateInputs = ["launch_date"];
    dateInputs.forEach((fieldId) => {
      const field = document.getElementById(fieldId);
      if (field) {
        field.addEventListener("change", async () => await saveFormData());
        field.addEventListener("blur", validateForm);
      }
    });

    // Submit button
    const submitButton = document.getElementById("distribution-submit");
    if (submitButton) {
      // Prevent duplicate event listener attachment
      if (submitButton.dataset.initialized === "true") {
        console.log(
          "[Distribution] Submit button already initialized, skipping"
        );
        return;
      }
      submitButton.dataset.initialized = "true";

      submitButton.addEventListener("click", handleSubmit);
    }
  }

  /**
   * Save form data to localStorage
   */
  async function saveFormData() {
    const territoryRadio = document.querySelector(
      'input[name="territory"]:checked'
    );
    const launchRadio = document.querySelector(
      'input[name="launch_timing"]:checked'
    );

    const getInputValue = (id, fallback = "") => {
      const element = document.getElementById(id);
      return element ? element.value : fallback;
    };

    const step4Data = {
      territory: territoryRadio ? territoryRadio.value : "",
      custom_territory: getInputValue("custom_territory"),
      launch_timing: launchRadio ? launchRadio.value : "",
      launch_date: getInputValue("launch_date"),
      launch_time: getInputValue("launch_time", "00:00"),
      distribution_notes: getInputValue("distribution_notes"),
      // Note: Contact information is collected on the review page (step 5)
    };

    sessionManager.saveStepData(4, step4Data);
    console.log("Distribution data saved:", step4Data);

    // WAIT for persistence to complete
    await persistStepData(4, step4Data);
    console.log("[Step 4] Successfully persisted to database");
  }

  /**
   * Validate form and enable/disable submit button
   */
  function validateForm() {
    const territoryRadio = document.querySelector(
      'input[name="territory"]:checked'
    );
    const launchRadio = document.querySelector(
      'input[name="launch_timing"]:checked'
    );
    const submitButton = document.getElementById("distribution-submit");

    let isValid = true;

    // Territory is required
    if (!territoryRadio) {
      isValid = false;
    }

    // If "other" territory selected, custom territory is required
    if (territoryRadio && territoryRadio.value === "other") {
      const customTerritoryInput = document.getElementById("custom_territory");
      const customTerritory = customTerritoryInput
        ? customTerritoryInput.value.trim()
        : "";
      if (!customTerritory) {
        isValid = false;
      }
    }

    // Launch timing is required
    if (!launchRadio) {
      isValid = false;
    }

    // If "specific_date" launch selected, date is required (time defaults to 00:00 midnight PST)
    if (launchRadio && launchRadio.value === "specific_date") {
      const launchDateInput = document.getElementById("launch_date");
      const launchDate = launchDateInput ? launchDateInput.value.trim() : "";
      if (!launchDate) {
        isValid = false;
      }
    }

    // Note: Contact information is validated on the review page, not here

    if (submitButton) {
      submitButton.disabled = !isValid;
    }
  }

  /**
   * Handle form submission
   */
  async function handleSubmit() {
    try {
      // Save form data and WAIT for persistence
      await saveFormData();

      // Validate
      const territoryRadio = document.querySelector(
        'input[name="territory"]:checked'
      );

      if (!territoryRadio) {
        showNotification("Please select a distribution territory", "warning");
        return;
      }

      if (territoryRadio.value === "other") {
        const customTerritoryInput = document.getElementById(
          "custom_territory"
        );
        const customTerritory = customTerritoryInput
          ? customTerritoryInput.value.trim()
          : "";
        if (!customTerritory) {
          showNotification("Please specify your custom territories", "warning");
          if (customTerritoryInput) {
            customTerritoryInput.focus();
          }
          return;
        }
      }

      // Navigate to review page ONLY after successful persistence
      sessionManager.navigateWithSession(`${getApplyBasePath()}/review`);
    } catch (error) {
      console.error("[Step 4] Failed to save or persist data:", error);
      showNotification("Failed to save your data. Please try again.", "error");
      // Don't navigate - let user retry
    }
  }

  /**
   * Setup navigation
   */
  function setupNavigation() {
    const backButton = document.getElementById("back-button");
    if (backButton) {
      backButton.addEventListener("click", async (e) => {
        e.preventDefault();
        await saveFormData();
        const step1Data = sessionManager.getStepData(1);
        let targetUrl = `${getApplyBasePath()}/customize`;
        if (step1Data.imdb_id) {
          targetUrl += `?imdb_id=${encodeURIComponent(step1Data.imdb_id)}`;
        }
        sessionManager.navigateWithSession(targetUrl);
      });
    }
  }

  /**
   * Show notification
   */
  function showNotification(message, type) {
    const notification = document.createElement("div");
    notification.className = `callout ${type} has-icon`;
    notification.style.cssText =
      "position: fixed; top: 20px; right: 20px; z-index: 9999; max-width: 400px; animation: slideIn 0.3s ease;";
    notification.innerHTML = `
      <div class="callout-icon">
        <i class="fas fa-${
          type === "success"
            ? "check-circle"
            : type === "error"
            ? "exclamation-circle"
            : "info-circle"
        }"></i>
      </div>
      <div class="callout-content">
        <p>${String(message === null || message === undefined ? "" : message).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>
      </div>
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.animation = "slideOut 0.3s ease";
      setTimeout(() => notification.remove(), 300);
    }, 5000);
  }

  /**
   * Update progress bar
   */
  function updateProgress() {
    const progressBar = document.getElementById("progress-bar");
    if (progressBar) {
      progressBar.style.width = "80%"; // Step 4 of 5
    }
  }

  /**
   * Persist step data to database
   */
  async function persistStepData(stepNumber, formData) {
    try {
      const response = await fetch("/api/submissions/save-step", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          uuid: sessionManager.getSessionId(),
          step: stepNumber,
          form_data: formData,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        console.error(
          "[Step " + stepNumber + "] Database persistence failed:",
          result.message
        );
        return false;
      }

      console.log(
        "[Step " + stepNumber + "] Successfully persisted to database"
      );
      return true;
    } catch (error) {
      console.error(
        "[Step " + stepNumber + "] Error persisting to database:",
        error
      );
      throw error;
    }
  }

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
